// External Dependencies
import React, { useEffect, useState, useRef, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { FiUpload } from 'react-icons/fi'
import { IoArrowBackOutline, IoClose } from 'react-icons/io5'

// Components
import CustomToast from '../../components/Custom/CustomToast'
import PageHeader from '../../components/Custom/PageHeader/PageHeader'

// Hooks and Utils
import useGoBack from '../../hooks/useGoBack'
import useUserStore from '../../utils/state/useUserStore'
import axiosInterceptorInstance from '../../utils/axios/axiosInterceptorInstance'

import configManager from '../../config/configManager'

const PatternEdit = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const GoBack = useGoBack()
  const configData = configManager()

  const userPermissions = useUserStore((state) => state.user) || []

  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  const categories = useMemo(
    () => [
      'Brick Bond',
      'Paving',
      'Parquetry',
      'Geometric',
      'Organic',
      'Random',
      'Roofing',
    ],
    [],
  )

  const memberships = useMemo(() => ['Free', 'Premium', 'Pro'], [])

  const [formData, setFormData] = useState({
    materialName: '',
    selectedCategory: '',
    selectedMemberships: [],
    height: '',
    width: '',
    maxTileWidth: '',
    rowMultiple: '',
    columnMultiple: '',
    materialFile: null,
    materialPreview: null,
  })

  const [errors, setErrors] = useState({})

  useEffect(() => {
    const fetchPattern = async () => {
      try {
        const { data } = await axiosInterceptorInstance.get(
          `/patterns/details/${id}`,
        )
        if (!data.success) {
          throw new Error('Failed to fetch pattern data')
        }

        console.log('Fetched pattern data:', data.data) // Debug log

        // Safely extract values with fallbacks
        const patternData = data.data
        const definition = patternData.definition || {}

        setFormData({
          materialName: patternData.name || '',
          selectedCategory: patternData.categories?.toString() || '',
          selectedMemberships: Array.isArray(patternData.membership)
            ? patternData.membership
            : patternData.membership
              ? [patternData.membership]
              : [],
          height: definition.height || '',
          width: definition.width || '',
          maxTileWidth: definition.maxTileWidth || '',
          // Important: rowMultiple and columnMultiple might be at root level or in definition
          rowMultiple: patternData.rowMultiple || definition.rowMultiple || '',
          columnMultiple: patternData.columnMultiple || definition.columnMultiple || '',
          materialFile:
            `${configData.CDN_BASE_URL}/patterns/${patternData.stringId}.svg` ||
            '',
          materialPreview:
            `${configData.CDN_BASE_URL}/patterns/${patternData.stringId}.svg` ||
            '',
        })
      } catch (error) {
        console.error('Error fetching pattern:', error)
        CustomToast({
          message:
            error.response?.data?.message || 'Failed to load pattern data',
          type: 'error',
        })
        navigate('/pattern')
      } finally {
        setIsLoading(false)
      }
    }

    if (id) fetchPattern()
  }, [id, navigate, configData.CDN_BASE_URL])

  // Form Field Change Handler
  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }))
    setErrors((prev) => ({ ...prev, [field]: '' }))
  }

  // Membership Checkbox Change Handler
  const handleMembershipChange = (membership) => (e) => {
    setFormData((prev) => {
      const selected = prev.selectedMemberships.includes(membership)
        ? prev.selectedMemberships.filter((m) => m !== membership)
        : [...prev.selectedMemberships, membership]
      return { ...prev, selectedMemberships: selected }
    })
    setErrors((prev) => ({ ...prev, selectedMemberships: '' }))
  }

  // Form Validation
  const validateInputs = () => {
    const newErrors = {}
    const {
      materialName,
      selectedCategory,
      selectedMemberships,
    } = formData

    if (!materialName?.trim()) {
      newErrors.materialName = 'Pattern name is required'
    }
    if (!selectedCategory) {
      newErrors.selectedCategory = 'Please select a category'
    }
    if (!selectedMemberships || selectedMemberships.length === 0) {
      newErrors.selectedMemberships = 'Please select at least one membership'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Form Submission Handler
  const handleSubmit = async () => {
    if (!userPermissions.includes('pattern:update')) {
      CustomToast({
        message: 'You do not have permission to update patterns',
        type: 'error',
      })
      return
    }

    if (!validateInputs()) return

    setIsSaving(true)

    try {
      const payload = {
        name: formData.materialName,
        category: formData.selectedCategory,
        membership: formData.selectedMemberships,
      }

      // Only include fields that have values
      if (formData.width) payload.width = formData.width
      if (formData.height) payload.height = formData.height
      if (formData.maxTileWidth) payload.maxTileWidth = formData.maxTileWidth
      if (formData.rowMultiple) payload.rowMultiple = formData.rowMultiple
      if (formData.columnMultiple) payload.columnMultiple = formData.columnMultiple

      console.log('Updating pattern with payload:', payload) // Debug log

      await axiosInterceptorInstance.patch(`/patterns/update/${id}`, payload)
      CustomToast({
        message: 'Pattern updated successfully',
        type: 'success',
      })
      navigate('/pattern')
    } catch (error) {
      console.error('Update error:', error)
      CustomToast({
        message: error.response?.data?.message || 'Failed to update pattern',
        type: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }

  // Utility Functions
  const renderError = (field) =>
    errors[field] && (
      <p className='text-red-500 text-sm mt-1'>{errors[field]}</p>
    )

  const inputClass = (errorField) =>
    `w-full border ${
      errors[errorField] ? 'border-red-500' : 'border-gray-300'
    } rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-prime`

  if (isLoading) {
    return (
      <div className='flex justify-center items-center h-64'>
        <p className='text-gray-500 text-lg'>Loading pattern details...</p>
      </div>
    )
  }

  return (
    <div>
      <PageHeader>
        <div className='flex gap-2 items-center justify-between w-full'>
          <div className='flex gap-2 items-center font-medium'>
            <button
              onClick={GoBack}
              className='h-9 w-9 flex justify-center items-center rounded-full text-xl cursor-pointer transition-all duration-300 hover:text-white hover:bg-prime'
              aria-label='Go back'
            >
              <IoArrowBackOutline />
            </button>
            <h1 className='text-2xl'>Edit Pattern</h1>
          </div>
        </div>
      </PageHeader>

      <div className='flex flex-col items-center sm:gap-4 p-4'>
        {/* Row 1: Category and Memberships */}
        <div className='w-full sm:flex justify-center gap-6'>
          <div className='w-full sm:w-[500px]'>
            <label className='block text-sm font-medium mt-2'>
              Select Category:
            </label>
            <select
              className={inputClass('selectedCategory')}
              value={formData.selectedCategory}
              onChange={handleChange('selectedCategory')}
            >
              <option value=''>-- Select Category --</option>
              {categories.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            {renderError('selectedCategory')}
          </div>

          <div className='w-full sm:w-[500px]'>
            <label className='block text-sm font-medium mt-2'>
              Select Memberships:
            </label>
            <div className='flex flex-wrap gap-4 mt-2'>
              {memberships.map((membership) => (
                <label key={membership} className='flex items-center gap-2'>
                  <input
                    type='checkbox'
                    checked={formData.selectedMemberships.includes(membership)}
                    onChange={handleMembershipChange(membership)}
                    className='w-4 h-4 text-prime focus:ring-prime rounded'
                  />
                  <span>{membership}</span>
                </label>
              ))}
            </div>
            {renderError('selectedMemberships')}
          </div>
        </div>

        {/* Row 2: Pattern Name and Width */}
        <div className='w-full sm:flex justify-center gap-6'>
          <div className='w-full sm:w-[500px]'>
            <label className='block text-sm font-medium mt-2'>
              Pattern Name:
            </label>
            <input
              type='text'
              placeholder='Enter pattern name'
              className={inputClass('materialName')}
              value={formData.materialName}
              onChange={handleChange('materialName')}
            />
            {renderError('materialName')}
          </div>

          <div className='w-full sm:w-[500px]'>
            <label className='block text-sm font-medium mt-2'>
              Pattern Width:
            </label>
            <input
              type='text'
              placeholder='Auto-calculated'
              className={inputClass('width')}
              value={formData.width}
              onChange={handleChange('width')}
              disabled={!formData.width} // Disable if auto-calculated
            />
            {renderError('width')}
            {!formData.width && (
              <p className='text-xs text-gray-500 mt-1'>
                Auto-calculated from SVG
              </p>
            )}
          </div>
        </div>

        {/* Row 3: Height and Max Tile Width */}
        <div className='w-full sm:flex justify-center gap-6'>
          <div className='w-full sm:w-[500px]'>
            <label className='block text-sm font-medium mt-2'>
              Pattern Height:
            </label>
            <input
              type='text'
              placeholder='Auto-calculated'
              className={inputClass('height')}
              value={formData.height}
              onChange={handleChange('height')}
              disabled={!formData.height}
            />
            {renderError('height')}
            {!formData.height && (
              <p className='text-xs text-gray-500 mt-1'>
                Auto-calculated from SVG
              </p>
            )}
          </div>

          <div className='w-full sm:w-[500px]'>
            <label className='block text-sm font-medium mt-2'>
              Max Tile Width:
            </label>
            <input
              type='text'
              placeholder='Auto-calculated'
              className={inputClass('maxTileWidth')}
              value={formData.maxTileWidth}
              onChange={handleChange('maxTileWidth')}
              disabled={!formData.maxTileWidth}
            />
            {renderError('maxTileWidth')}
            {!formData.maxTileWidth && (
              <p className='text-xs text-gray-500 mt-1'>
                Auto-calculated from SVG
              </p>
            )}
          </div>
        </div>

        {/* Row 4: Row Multiple and Column Multiple */}
        <div className='w-full sm:flex justify-center gap-6'>
          <div className='w-full sm:w-[500px]'>
            <label className='block text-sm font-medium mt-2'>
              Row Multiple:
            </label>
            <input
              type='text'
              placeholder='Auto-calculated'
              className={inputClass('rowMultiple')}
              value={formData.rowMultiple}
              onChange={handleChange('rowMultiple')}
              disabled={!formData.rowMultiple}
            />
            {renderError('rowMultiple')}
            {!formData.rowMultiple && (
              <p className='text-xs text-gray-500 mt-1'>
                Auto-calculated from SVG
              </p>
            )}
          </div>

          <div className='w-full sm:w-[500px]'>
            <label className='block text-sm font-medium mt-2'>
              Column Multiple:
            </label>
            <input
              type='text'
              placeholder='Auto-calculated'
              className={inputClass('columnMultiple')}
              value={formData.columnMultiple}
              onChange={handleChange('columnMultiple')}
              disabled={!formData.columnMultiple}
            />
            {renderError('columnMultiple')}
            {!formData.columnMultiple && (
              <p className='text-xs text-gray-500 mt-1'>
                Auto-calculated from SVG
              </p>
            )}
          </div>
        </div>

        {/* File Preview */}
        <div className='w-full sm:w-[500px]'>
          <label className='block text-sm font-medium mt-2'>
            Pattern Preview:
          </label>
          <div className='relative'>
            <div
              className={`relative w-full aspect-[9/5] rounded-lg border border-gray-300 flex items-center justify-center overflow-hidden`}
            >
              {formData.materialPreview ? (
                <img
                  src={formData.materialPreview}
                  alt='Pattern Preview'
                  className='object-contain w-full h-full'
                />
              ) : (
                <div className='flex flex-col gap-4 items-center text-gray-400'>
                  <FiUpload className='text-4xl' />
                  <span>No preview available</span>
                </div>
              )}
            </div>
            {renderError('materialFile')}
          </div>
        </div>

        {/* Submit Button */}
        <button
          disabled={isSaving}
          onClick={handleSubmit}
          className='w-full sm:w-[500px] py-3 rounded-md text-prime bg-white border border-prime hover:bg-prime hover:text-white transition-all duration-300 disabled:bg-opacity-60 disabled:pointer-events-none mt-4'
        >
          {isSaving ? 'Saving...' : 'Update Pattern'}
        </button>
      </div>
    </div>
  )
}

export default PatternEdit